// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import type { DispatchType } from "../../global/types/redux";
import type { ErrorsType, PasswordStrengthType } from "../types/UserFormType";
import { ErrosDisplay, checkPassword, setErrorMsg, checkEmail } from "../containers/ErrosDisplay";
import { SingleTextField, CancelButton, SubmitButton, CompleteMessage } from "imagus-components";
import { PasswordStrengthBox, checkPswStrength } from "../../global/components/PasswordStrength";
import UserAuth from "../../global/containers/UserAuth";
import RedirectoIndex from "./RedirectoIndex";
import { userSetNewPassword } from "../actions/profileActions";
import BlueLogoImage from "../../../images/VixVizion.svg";
import history from "../../../history";

import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import EmailIcon from "@material-ui/icons/Email";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";

const DEBOUNCETIME = 500;

type ForgotStatesType = {
  errors: ErrorsType,
  username: string,
  password: string,
  password2: string,
  verificationCode: string,
  loading: boolean,
  passwordstrength: PasswordStrengthType,
  completed: boolean
};

class ForgotForm extends Component<*, ForgotStatesType> {
  timeout: number;
  constructor(props) {
    super(props);
    this.submit = this.submit.bind(this);
    this.state = {
      errors: {},
      loading: false,
      username: props.match.params.token ? props.match.params.token : "",
      password: "",
      password2: "",
      verificationCode: "",
      passwordstrength: {
        uppercase: false,
        lowercase: false,
        number: false,
        length: false
      },
      completed: false
    };
  }

  handleChange = (e: Event & { currentTarget: HTMLInputElement }): * => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    this.setState((state: ForgotStatesType) => ({
      [name]: value
    }));

    //password strength check
    if (name === "password") {
      this.setState({
        passwordstrength: checkPswStrength(this.state.passwordstrength, value)
      });
    }

    //clear confirmation_code error msg
    if (name === "verificationCode") {
      if (this.state.errors.confirmation_code !== "") {
        this.setState({ errors: setErrorMsg(this.state.errors, "confirmation_code", "", true) });
      }
    }

    if (name === "password" || name === "password2") {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(function () {
        this.setState({ errors: checkPassword(this.state.errors, this.state.password, this.state.password2) });
      }.bind(this), DEBOUNCETIME);
    }

    if (name === "username") {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(function (val: string) {
        this.setState({ errors: checkEmail(this.state.errors, val) });
      }.bind(this, value), DEBOUNCETIME);
    }

  }

  submit = async (e: Event): * => {
    e.preventDefault();
    e.stopPropagation();

    this.setState((): * => ({ "loading": true }));

    const { stage, username, password, password2, verificationCode, errors, passwordstrength } = this.state;

    if (Object.keys(errors).length === 0 && !Object.values(this.state.passwordstrength).includes(false)) {
      this.props.actions.userSetNewPassword(
        encodeURI(username.trim()),
        password.trim(),
        verificationCode.trim(),
        (): * => {
          this.setState((): * => ({ "completed": true }));
        },
        (errors: ErrorsType): * => {
          this.setState({
            errors: errors
          });
        }
      );
    }
    this.setState((): * => ({ "loading": false }));
  }

  render(): React$Element<*> {
    const { errors, loading, stage } = this.state;
    if (this.state.completed) {
      return (
        <Grid container spacing={24}>
          <Grid item xs={1}></Grid>
          <Grid item xs={10}>
            <CompleteMessage
              title="Done"
              content="Your password has been reset successfully.<br>Please log in with new password."
              onClick={() => window.location.href = "/login"}
              isPageCenter
            />
          </Grid>
        </Grid>
      );
    }
    else {
      return (
        <div className="wrapper">
          <AppBar position="static" color="primary">
            <Toolbar>
              <EmailIcon className="light-icon" />
              <Typography variant="h6" color="inherit" className="white-title form-text">
                Please check your Email<br /> for your verification code.
              </Typography>
            </Toolbar>
          </AppBar>
          <img src={BlueLogoImage} className="form-logo top-margin-15" style={{ float: "left" }} />
          <div style={{ clear: "both" }}></div>

          <div className="form-text">
            <Typography variant="h5" className="green-title" style={{ marginBottom: "30px" }}>Reset Your Password</Typography>
          </div>
          <form onSubmit={(!loading && this.submit) || undefined}>
            <Grid container spacing={8}>
              <Grid item xs={1} className="icon-block">
                <EmailIcon className="green-icon medium-icon" />
              </Grid>
              <Grid item xs={11}>
                <SingleTextField
                  isRequired={true}
                  label="Email Address"
                  name="username"
                  value={this.state.username}
                  onChange={this.handleChange}
                  inpuType="text"
                  isInvalid={errors.username !== undefined}
                />
              </Grid>
              <Grid item xs={1} className="icon-block">
                <VerifiedUserIcon className="green-icon medium-icon" />
              </Grid>
              <Grid item xs={11}>
                <SingleTextField
                  isRequired={true}
                  label="Verification Code"
                  name="verificationCode"
                  value={this.state.verificationCode}
                  onChange={this.handleChange}
                  inpuType="text"
                />
              </Grid>
              <Grid item xs={1} className="icon-block">
                <LockOutlinedIcon className="green-icon medium-icon" />
              </Grid>
              <Grid item xs={11} md={5}>
                <SingleTextField
                  isRequired={true}
                  label="New Password"
                  name="password"
                  value={this.state.password}
                  onChange={this.handleChange}
                  inpuType="password"
                />
              </Grid>
              <Grid item xs={1}></Grid>
              <Grid item xs={11} md={5}>
                <SingleTextField
                  isRequired={true}
                  label="Confirm Password"
                  name="password2"
                  value={this.state.password2}
                  onChange={this.handleChange}
                  inpuType="password"
                />
              </Grid>
              <Grid item xs={12}>
                <PasswordStrengthBox
                  passwordstrength={this.state.passwordstrength}
                  password={this.state.password}
                />
              </Grid>
              <Grid item xs={12}>
                <ErrosDisplay errors={errors} form="reset password" />
              </Grid>
              <Grid item xs={12}>
                <div className="form-right-btn-block">
                  <CancelButton onClick={() => history.push("/login")} />
                  <SubmitButton
                    loading={loading}
                    title="Reset Password"
                    disabled={Object.keys(errors).length > 0 || Object.values(this.state.passwordstrength).includes(false)}
                  />
                </div>
              </Grid>
            </Grid>
          </form>
        </div>
      );
    };
  }
}

const mapDispatchToProps = (dispatch: DispatchType): DispatchType => ({
  actions: bindActionCreators({ userSetNewPassword }, dispatch)
});

const ReduxForgotForm = connect(null, mapDispatchToProps)(ForgotForm);

class ForgotUser extends Component<*> {
  render(): React$Element<*> {
    return (
      <UserAuth principal='user' unAuthChildren={<ReduxForgotForm match={this.props.match} />}>
        <RedirectoIndex />
      </UserAuth>
    );
  }
}

export default ForgotUser;
